import { newVector, Vector } from '@lessonup/pins-shared';
import React from 'react';
import { addVectors } from '../../utils';
import { Path } from './svgPath';

export interface PieSegmentProps {
  /** Center point of the pie in px. */
  center: Vector;

  /** Radius of the pie in px. */
  radius: number;

  /**
   * Angle at which the pie segment starts.
   *
   * Angle is provided in CSS turns (0-1), with 0 representing the top of the
   * pie.
   */
  startTurn: number;

  /** Angle of the pie segment itself. */
  sweepTurn: number;

  /** Color of the pie segment. Used only when drawing the spinning segments and not the highliter */
  fillColor?: string;
}

/** SVG path element corresponding to a single slice of a pie chart. */
export function PieSegment({ center, radius, startTurn, sweepTurn, fillColor }: PieSegmentProps) {
  const strokeColor = fillColor ? '#14171F40' : undefined;
  const strokeWidth = fillColor ? 1 : undefined;

  if (sweepTurn >= 1) {
    return (
      <circle cx={center.x} cy={center.y} r={radius} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} />
    );
  }

  // We want angle 0 to represent the top of the circle, but in math angle 0
  // represents the right side of the circle, so we adjust for that here.
  const adjustedStartTurn = startTurn - 0.25;

  const startArc = addVectors(center, polarToCartesian(radius, adjustedStartTurn));
  const endArc = addVectors(center, polarToCartesian(radius, adjustedStartTurn + sweepTurn));

  const path = Path.closedPath([Path.moveTo(center), Path.lineTo(startArc), Path.circleArc(radius, sweepTurn, endArc)]);

  return <path d={path} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth} />;
}

function polarToCartesian(radius: number, turn: number) {
  const radians = turn * 2 * Math.PI;
  return newVector(radius * Math.cos(radians), radius * Math.sin(radians));
}
